import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  IconButton,
  Tooltip,
  Box,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Typography,
  Divider,
  CircularProgress,
  SvgIcon,
  Chip,
  Badge,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSignOutAlt } from '@fortawesome/free-solid-svg-icons';
import { httpOnlySecurityService } from '../../application/iam';
import { useUser } from '../../hooks/useUser';
import { UserRoles } from '../../common/constants/userConstants';
import { useLocation } from 'react-router-dom';
import { isPublicRoute } from '../../common/utils/routeUtils';
import { useHelp } from '../../application/context/HelpContext';

// Custom User Icon component
const UserIcon = () => (
  <SvgIcon sx={{ width: 28, height: 32, fontSize: 'inherit' }} viewBox='0 0 28 32'>
    <path
      d='M14 16C11.125 16 8.5 14.5 7.0625 12C5.625 9.5625 5.625 6.5 7.0625 4C8.5 1.5625 11.125 0 14 0C16.8125 0 19.4375 1.5625 20.875 4C22.3125 6.5 22.3125 9.5625 20.875 12C19.4375 14.5 16.8125 16 14 16ZM11.125 19H16.8125C23 19 28 24 28 30.1875C28 31.1875 27.125 32 26.125 32H1.8125C0.8125 32 0 31.1875 0 30.1875C0 24 4.9375 19 11.125 19Z'
      fill='#CDD6D9'
    />
  </SvgIcon>
);

const QuestionIcon = () => (
  <svg width='23' height='36' viewBox='0 0 23 36' fill='none' xmlns='http://www.w3.org/2000/svg'>
    <path
      d='M5.25 10.5C5.25 11.9062 4.07812 13 2.75 13C1.34375 13 0.25 11.9062 0.25 10.5C0.25 5.03125 4.70312 0.5 10.25 0.5H12.75C18.2188 0.5 22.75 5.03125 22.75 10.5V10.8125C22.75 14.25 20.9531 17.375 18.1406 19.25L14.8594 21.3594C14.3125 21.6719 14 22.2969 14 22.9219V23C14 24.4062 12.8281 25.5 11.5 25.5C10.0938 25.5 9 24.4062 9 23V22.9219C9 20.5781 10.1719 18.3906 12.125 17.1406L15.4062 15.0312C16.8125 14.0938 17.75 12.5312 17.75 10.8125V10.5C17.75 7.76562 15.4844 5.5 12.75 5.5H10.25C7.4375 5.5 5.25 7.76562 5.25 10.5ZM11.5 35.5C10.3281 35.5 9.3125 34.9531 8.76562 33.9375C8.21875 33 8.21875 31.8281 8.76562 30.8125C9.3125 29.875 10.3281 29.25 11.5 29.25C12.5938 29.25 13.6094 29.875 14.1562 30.8125C14.7031 31.8281 14.7031 33 14.1562 33.9375C13.6094 34.9531 12.5938 35.5 11.5 35.5Z'
      fill='#CDD6D9'
    />
  </svg>
);

const SettingsIcon = () => (
  <svg width='32' height='32' viewBox='0 0 32 32' fill='none' xmlns='http://www.w3.org/2000/svg'>
    <path
      d='M30.9375 10.4385C31.1875 11.001 31 11.5635 30.5625 12.001L27.875 14.4385C27.9375 14.9385 27.9375 15.501 27.9375 16.001C27.9375 16.5635 27.9375 17.126 27.875 17.626L30.5625 20.0635C31 20.4385 31.1875 21.0635 30.9375 21.626C30.6875 22.376 30.375 23.0635 30 23.751L29.6875 24.251C29.25 24.9385 28.8125 25.626 28.3125 26.1885C27.9375 26.6885 27.3125 26.8135 26.75 26.626L23.3125 25.5635C22.4375 26.1885 21.5 26.6885 20.5625 27.126L19.75 30.6885C19.625 31.251 19.1875 31.6885 18.625 31.8135C17.75 31.9385 16.875 32.001 15.9375 32.001C15.0625 32.001 14.1875 31.9385 13.3125 31.8135C12.75 31.6885 12.3125 31.251 12.1875 30.6885L11.375 27.126C10.375 26.6885 9.5 26.1885 8.625 25.5635L5.1875 26.626C4.625 26.8135 4 26.6885 3.625 26.251C3.125 25.626 2.6875 24.9385 2.25 24.251L1.9375 23.751C1.5625 23.0635 1.25 22.376 1 21.626C0.75 21.0635 0.9375 20.501 1.375 20.0635L4.0625 17.626C4 17.126 4 16.5635 4 16.001C4 15.501 4 14.9385 4.0625 14.4385L1.375 12.001C0.9375 11.5635 0.75 11.001 1 10.4385C1.25 9.68848 1.5625 9.00098 1.9375 8.31348L2.25 7.81348C2.6875 7.12598 3.125 6.43848 3.625 5.81348C4 5.37598 4.625 5.25098 5.1875 5.43848L8.625 6.50098C9.5 5.87598 10.4375 5.31348 11.375 4.93848L12.1875 1.37598C12.3125 0.813477 12.75 0.375977 13.3125 0.250977C14.1875 0.125977 15.0625 0.000976562 16 0.000976562C16.875 0.000976562 17.75 0.125977 18.625 0.250977C19.1875 0.313477 19.625 0.813477 19.75 1.37598L20.5625 4.93848C21.5625 5.31348 22.4375 5.87598 23.3125 6.50098L26.75 5.43848C27.3125 5.25098 27.9375 5.37598 28.3125 5.81348C28.8125 6.43848 29.25 7.12598 29.6875 7.81348L30 8.31348C30.375 9.00098 30.6875 9.68848 31 10.4385H30.9375ZM16 21.001C17.75 21.001 19.375 20.0635 20.3125 18.501C21.1875 17.001 21.1875 15.0635 20.3125 13.501C19.375 12.001 17.75 11.001 16 11.001C14.1875 11.001 12.5625 12.001 11.625 13.501C10.75 15.0635 10.75 17.001 11.625 18.501C12.5625 20.0635 14.1875 21.001 16 21.001Z'
      fill='#CDD6D9'
    />
  </svg>
);

/** Resolves a download URL and filename from a MarkdownContent entry.
 *  Prefers the first item in attachmentList; falls back to parsing a
 *  `[filename](url)` markdown link from the content field. */
function resolveAttachment(entry: { attachmentList?: { url: string; filename?: string }[]; content?: string }): { url: string; filename: string } | null {
  if (entry.attachmentList && entry.attachmentList.length > 0) {
    return { url: entry.attachmentList[0].url, filename: entry.attachmentList[0].filename || '' };
  }
  const match = entry.content?.trim().match(/^\[([^\]]*)\]\(([^)]+)\)/);
  if (match) return { url: match[2], filename: match[1] };
  return null;
}

/** Triggers a browser file download via a temporary anchor element. */
function triggerDownload(url: string, filename: string): void {
  const a = document.createElement('a');
  a.href = url;
  a.target = '_blank';
  a.rel = 'noopener noreferrer';
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

const RightHeader: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const isPublic = isPublicRoute(location.pathname);
  
  // Read user data from cache only - UserInformationProvider handles fetching
  // This prevents duplicate requests - we only read from React Query cache
  const { data: userInfo, isLoading: userLoading, isError: userError } = useUser(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  // Memoize values derived from userInfo to prevent unnecessary re-renders
  const isAdmin = useMemo(
    () => userInfo?.role === UserRoles.ADMIN || userInfo?.role === UserRoles.SUPER_USER,
    [userInfo?.role]
  );

  const isAdminOnly = useMemo(
    () => userInfo?.role === UserRoles.ADMIN,
    [userInfo?.role]
  );

  const isAuthenticated = useMemo(() => !!userInfo && !userError, [userInfo, userError]);

  // Function to get role display text
  const getRoleDisplayText = useMemo(() => {
    if (!userInfo?.role) return '';

    switch (userInfo.role) {
      case UserRoles.ADMIN:
        return UserRoles.ADMIN;
      case UserRoles.SUPER_USER:
        return UserRoles.SUPER_USER;
      case UserRoles.USER:
        return UserRoles.USER;
      default:
        return userInfo.role;
    }
  }, [userInfo?.role]);

  // Function to get role chip color
  const getRoleChipColor = useMemo(() => {
    if (!userInfo?.role) return 'default';

    switch (userInfo.role) {
      case UserRoles.ADMIN:
        return 'error';
      case UserRoles.SUPER_USER:
        return 'secondary';
      case UserRoles.USER:
        return 'primary';
      default:
        return 'default';
    }
  }, [userInfo?.role]);

  const { helpContent, globalEntries } = useHelp();

  const handleProfileClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleSettingClick = () => {
    navigate('/settings');
  };

  const handleHelpClick = () => {
    navigate('/help');
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleClose();
    httpOnlySecurityService?.logout();
  };

  const handleProfileNavigate = () => {
    handleClose();
    navigate('/profile');
  };

  const handleHelpDocumentDownloadNavigate = () => {
    handleClose();
    const searchIn = globalEntries.length > 0 ? globalEntries : helpContent;
    const entry = searchIn.find(
      (c) => c.datatestid === 'help-document-download' && c.moduleid === 'Global'
    );
    if (!entry) return;
    const attachment = resolveAttachment(entry);
    if (!attachment) return;
    triggerDownload(attachment.url, attachment.filename);
  };

  const iconButtonStyle = {};

  // Show loading state while checking authentication or user is loading
  if (userLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 1 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  // Don't render anything if not authenticated
  if (!isAuthenticated || !userInfo) {
    return null;
  }

  return (
    <Box
      className='flex flex-col md:flex-row items-end justify-end gap-4 w-full'
      sx={{ alignItems: 'center' }}
      data-testid='right-header-container'
    >
      <IconButton
        onClick={handleSettingClick}
        size='large'
        sx={iconButtonStyle}
        data-testid='right-header-settings-button'
      >
        <SettingsIcon />
      </IconButton>
      <Divider
        orientation='vertical'
        flexItem
        sx={{ strokeWidth: 0.5, borderColor: '#6A848E', color: '#6A848E' }}
      />

      <Tooltip title={`${userInfo.email || ''} (${userInfo.designatedOperator})`}>
        <Badge
          overlap='circular'
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          badgeContent={
            <Chip
              label={userInfo.designatedOperator}
              color='success'
              size='small'
              sx={{
                height: '16px',
                fontSize: '9px',
                '& .MuiChip-label': {
                  px: 0.8,
                  py: 0.2,
                },
              }}
            />
          }
        >
          <IconButton
            onClick={handleProfileClick}
            size='large'
            sx={iconButtonStyle}
            data-testid='right-header-profile-button'
          >
            <UserIcon />
          </IconButton>
        </Badge>
      </Tooltip>

      {isAdminOnly && (
        <>
          <Divider
            orientation='vertical'
            flexItem
            sx={{ strokeWidth: 0.5, borderColor: '#6A848E', color: '#6A848E' }}
          />
          <Tooltip title={t('header.help', 'Help')}>
            <IconButton
              onClick={handleHelpClick}
              size='large'
              sx={iconButtonStyle}
              data-testid='right-header-help-button'
            >
              <QuestionIcon />
            </IconButton>
          </Tooltip>
        </>
      )}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        onClick={handleClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        data-testid='right-header-profile-menu'
      >
        <Box sx={{ px: 2, py: 1 }}>
          <Typography
            variant='body1'
            sx={{ color: 'text.primary', fontStyle: 'italic', fontSize: '10.5px' }}
          >
            Hi {userInfo.email}
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.5 }}>
            <Chip
              label={getRoleDisplayText}
              color={getRoleChipColor}
              size='small'
              sx={{
                height: '20px',
                fontSize: '10px',
                '& .MuiChip-label': {
                  px: 1,
                },
              }}
            />
            <Chip
              label={userInfo.designatedOperator}
              color='success'
              size='small'
              sx={{
                ml: 1,
                height: '20px',
                fontSize: '10px',
                '& .MuiChip-label': {
                  px: 1,
                },
              }}
            />
          </Box>
        </Box>
        <Divider />
        <MenuItem
          onClick={handleProfileNavigate}
          data-testid='right-header-profile-menu-item'
          sx={{ gap: '8px' }}
        >
          <ListItemText
            primary={t('header.profile')}
            primaryTypographyProps={{ sx: { fontSize: '10.5px' } }}
          />
        </MenuItem>
        <MenuItem
          onClick={handleHelpDocumentDownloadNavigate}
          data-testid='help-document-download'
          sx={{ gap: '8px' }}
        >
          <ListItemText
            primary={t('header.helpDocumentDownload', 'Help document download')}
            primaryTypographyProps={{ sx: { fontSize: '10.5px' } }}
          />
        </MenuItem>
        <MenuItem
          onClick={handleLogout}
          data-testid='right-header-logout-menu-item'
          sx={{ gap: '8px' }}
        >
          <ListItemIcon sx={{ minWidth: '10.5px !important' }}>
            <FontAwesomeIcon icon={faSignOutAlt} style={{ fontSize: '10.5px' }} />
          </ListItemIcon>
          <ListItemText
            primary={t('header.logout')}
            primaryTypographyProps={{ sx: { fontSize: '10.5px' } }}
          />
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default RightHeader;
